import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TransactionTypeListFilter } from './transaction-type-grid.filter';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { Observable } from 'rxjs';
import { TransactionTypeService } from '../../services/transaction-type.service';
import { tapResponse } from '@ngrx/operators';
import { SortDescriptor } from '@progress/kendo-data-query';
import { TransactionTypeMediatorService } from '../../services/transaction-type-mediator.service';
import { IOptionMenuItem } from '@kehe/phoenix-button';
import { ApiFailureText } from '@app/enum/alert-text.enum';

const defaultGridConfig: () => TransactionTypeListFilter = () => ({
  pageIndex: 0,
  pageCount: 25,
  includeAvailableCount: true,
  orderDirection: 1,
  sortName: 'effectiveDate',
  search: '',
});
export interface TransactionTypeListState {
  isLoading: boolean;
  transactionTypes: string[];
  totalRecords: number;
  pageSize: number;
  skip: number;
  actionLoading: boolean;
  gridData: any;
  gridConfig: TransactionTypeListFilter;
  selectedSort: null | SortDescriptor[];
  allData: any[];
  hasLoaded?: boolean;
  error?: string;
}

export const initialState: TransactionTypeListState = {
  isLoading: false,
  transactionTypes: [],
  totalRecords: 0,

  pageSize: 25,
  skip: 0,
  gridData: [],
  gridConfig: defaultGridConfig(),
  selectedSort: null,
  allData: [],
  hasLoaded: false,
  error: null,
  actionLoading: false,
};

@Injectable({ providedIn: 'root' })
export class TransactionTypeListStore extends ComponentStore<TransactionTypeListState> {
  public form: UntypedFormGroup;
  private mediator = inject(TransactionTypeMediatorService);
  private _transactionTypesService = inject(TransactionTypeService);
  readonly gridData$ = this.select((state) => state.gridData);
  private _formBuilder = inject(UntypedFormBuilder);

  constructor() {
    super(initialState);

    this.initForm();
    if (!this.get().hasLoaded) {
      this.getTransactionTypes({ filter: this.get().gridConfig });
    }
    this.subscribeToFromValueChanges();
    this.mediator.reload$.subscribe(() => {
      this.getTransactionTypes({ filter: this.get().gridConfig });
    });
  }

  initForm() {
    this.form = this._formBuilder.group({
      search: [''],
    });
  }
  private readonly _transactionTypeService = inject(TransactionTypeService);
  readonly allGridConfig$ = this.select((state) => state.gridConfig);

  readonly gridSearchConfig$ = this.select(
    this.allGridConfig$,
    (allGridConfig) => ({
      ...allGridConfig,
    }),
  );

  readonly gridConfig$ = this.select(this.gridSearchConfig$, (gridConfig) => ({
    filter: gridConfig,
  }));

  readonly selectedSort$ = this.select(this.allGridConfig$, (gridConfig) => [
    {
      field: gridConfig.sortName,
      dir: gridConfig.orderDirection === 1 ? 'desc' : 'asc',
    },
  ]);

  readonly vm$ = this.select(
    this.state$,
    this.selectedSort$,
    (state, selectedSort) => {
      const rows = (state.gridData ?? []) as any[];

      const gridDataWithMenus = rows.map((row) => ({
        ...row,
        menuOptions: this.buildMenuOptions(row),
      }));

      return {
        ...state,
        gridConfig: state.gridConfig,
        gridData: gridDataWithMenus,
        isLoading: state.isLoading,
        selectedSort,
      };
    },
  );

  reactivateEllipsesOption: IOptionMenuItem = {
    name: 'Reactivate',
    data: 'Reactivate',
    enabled: true,
    automationId: 'transaction-type-table-ellipsis-reactivate-option',
  };

  deactivateEllipsesOption: IOptionMenuItem = {
    name: 'Deactivate',
    data: 'Deactivate',
    enabled: true,
    automationId: 'transaction-type-table-ellipsis-deactivate-option',
  };

  downloadSupportingDocumentMenuOption: IOptionMenuItem = {
    name: 'Download Supporting Document',
    data: 'Download Supporting Document',
    enabled: true,
    automationId:
      'transaction-type-table-ellipsis-download-supporting-document-option',
  };

  baseEllipsesMenuOptions: IOptionMenuItem[] = [
    {
      name: 'Edit',
      data: 'Edit',
      enabled: true,
      automationId: 'transaction-type-table-ellipsis-edit-option',
    },
    {
      name: 'Download Template',
      data: 'Download Template',
      enabled: true,
      automationId: 'transaction-type-table-ellipsis-download-template-option',
    },
  ];

  private buildMenuOptions(row: any): IOptionMenuItem[] {
    let newRow: IOptionMenuItem[];
    newRow = row?.isActive
      ? [...this.baseEllipsesMenuOptions, this.deactivateEllipsesOption]
      : [...this.baseEllipsesMenuOptions, this.reactivateEllipsesOption];

    if (row.hasSupportingDocument && row.s3KeySupportingDocument) {
      const thirdOptionIndex = 2;
      newRow.splice(
        thirdOptionIndex,
        0,
        this.downloadSupportingDocumentMenuOption,
      );
    }
    return newRow;
  }

  setGridData = this.updater((state, payload: { gridData: any[] }) => {
    const { pageCount, pageIndex, sortName, orderDirection } = state.gridConfig;
    const sortedData = this.sortData(
      payload.gridData,
      sortName,
      orderDirection,
    );

    const start = pageIndex * pageCount;
    const end = start + pageCount;
    return {
      ...state,
      allData: sortedData,
      gridData: sortedData.slice(start, end),
      hasLoaded: true,
    };
  });

  setError = this.updater((state, error: string) => ({
    ...state,
    error,
  }));

  setTotalRecords = this.updater((state, totalRecords: number) => ({
    ...state,
    totalRecords,
  }));

  updateGridDataForPage = this.updater((state) => {
    const { pageCount, pageIndex, sortName, orderDirection } = state.gridConfig;
    const sortedData = this.sortData(state.allData, sortName, orderDirection);

    const start = pageIndex * pageCount;
    const end = start + pageCount;
    return {
      ...state,
      allData: sortedData,
      gridData: sortedData.slice(start, end),
    };
  });

  private transformTransactionTypes(data: any[]): any[] {
    return (
      data?.map((tt) => ({
        ...tt,
        codeValue: tt.name ? `${tt.name} (${tt.code})` : '--',
      })) ?? []
    );
  }

  subscribeToFromValueChanges() {
    this.form
      .get('search')
      ?.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        this.updateGridConfigSearch(value);
      });
  }

  updateGridConfigSearch = this.updater((state, search: string) => {
    const filteredData = state.allData.filter((item) => {
      if (!search) return true;
      const searchLower = search.trim().toLowerCase();
      const match = (val: any) =>
        val && val.toString().trim().toLowerCase().includes(searchLower);
      const matchCreatedOrUpdatedBy = item.lastUpdatedBy
        ? match(item.lastUpdatedBy)
        : match(item.createdBy);
      return (
        match(item.name) ||
        match(item.code) ||
        match(item.invoiceHeaderDescription) ||
        match(item.codeValue) ||
        match(item.s3KeyTemplate) ||
        match(item.costCenter) ||
        match(item.spendCategoryId) ||
        matchCreatedOrUpdatedBy
      );
    });

    const { pageCount, sortName, orderDirection } = state.gridConfig;
    const sortedData = this.sortData(filteredData, sortName, orderDirection);

    return {
      ...state,
      gridConfig: {
        ...state.gridConfig,
        search,
        pageIndex: 0,
      },
      gridData: sortedData.slice(0, pageCount),
      totalRecords: filteredData.length,
    };
  });

  updateGridConfigPagination = this.updater((state, event: PageChangeEvent) => {
    const newConfig = {
      ...state.gridConfig,
      pageIndex: event.skip / event.take,
      pageCount: event.take,
    };
    const { sortName, orderDirection, search } = state.gridConfig;
    let dataToPage = state.allData;

    if (search) {
      dataToPage = state.allData.filter((item) => {
        const searchLower = search.trim().toLowerCase();
        const match = (val: any) =>
          val && val.toString().trim().toLowerCase().includes(searchLower);
        const matchCreatedOrUpdatedBy = item.lastUpdatedBy
          ? match(item.lastUpdatedBy)
          : match(item.createdBy);
        return (
          match(item.name) ||
          match(item.code) ||
          match(item.invoiceHeaderDescription) ||
          match(item.codeValue) ||
          match(item.s3KeyTemplate) ||
          match(item.costCenter) ||
          match(item.spendCategoryId) ||
          matchCreatedOrUpdatedBy
        );
      });
    }

    let pagedData;
    if (!search) {
      pagedData = dataToPage.slice(
        newConfig.pageIndex * newConfig.pageCount,
        newConfig.pageIndex * newConfig.pageCount + newConfig.pageCount,
      );
    } else {
      const sortedData = this.sortData(dataToPage, sortName, orderDirection);
      pagedData = sortedData.slice(
        newConfig.pageIndex * newConfig.pageCount,
        newConfig.pageIndex * newConfig.pageCount + newConfig.pageCount,
      );
    }

    return {
      ...state,
      gridConfig: newConfig,
      gridData: pagedData,
    };
  });

  handlePageChange = this.updater((state, event: PageChangeEvent) => ({
    ...state,
    pageSize: event.take,
    skip: event.skip,
  }));

  getTransactionTypes = this.effect(
    (
      payload$: Observable<{
        filter: TransactionTypeListFilter;
      }>,
    ) =>
      payload$.pipe(
        tap(() => {
          this.patchState({ isLoading: true });
        }),
        switchMap(() => {
          return this._transactionTypeService.getTransactionTypes().pipe(
            tapResponse({
              next: (response) => {
                this.setGridData({
                  gridData: this.transformTransactionTypes(response.data),
                });
                this.updateGridDataForPage();
                this.patchState({ isLoading: false });
                this.setTotalRecords(response.availableCount);
              },
              error: () => {
                this.setError(ApiFailureText.Generic);
              },
            }),
          );
        }),
      ),
  );

  updateGridConfigSort = this.updater((state, sort: SortDescriptor) => {
    const newConfig = {
      ...state.gridConfig,
      sortName: sort.field,
      orderDirection: sort.dir === 'asc' ? 0 : 1,
      pageIndex: 0,
    };

    const { search } = state.gridConfig;
    let dataToSort = state.allData;

    if (search) {
      dataToSort = state.allData.filter((item) => {
        const searchLower = search.trim().toLowerCase();
        const match = (val: any) =>
          val && val.toString().trim().toLowerCase().includes(searchLower);
        return (
          match(item.name) ||
          match(item.code) ||
          match(item.invoiceHeaderDescription) ||
          match(item.codeValue) ||
          match(item.s3KeyTemplate) ||
          match(item.costCenter) ||
          match(item.lastUpdatedBy)
        );
      });
    }

    const sortedData = this.sortData(
      dataToSort,
      sort.field,
      newConfig.orderDirection,
    );
    const start = 0;
    const end = newConfig.pageCount;

    return {
      ...state,
      gridConfig: newConfig,
      gridData: sortedData.slice(start, end),
      totalRecords: dataToSort.length,
    };
  });

  private sortData(
    data: any[],
    sortName: string,
    orderDirection: number,
  ): any[] {
    if (!sortName || !data || data.length === 0) return data;

    const sortedData = [...data].sort((a, b) => {
      const aVal = a[sortName];
      const bVal = b[sortName];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }

      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });

    return orderDirection === 1 ? sortedData.reverse() : sortedData;
  }

  readonly deactivateTransactionType = this.effect<{
    code: string;
    onSuccess: () => void;
  }>((req$) =>
    req$.pipe(
      tap(() => this.patchState({ actionLoading: true })),
      switchMap(({ code, onSuccess }) =>
        this._transactionTypesService
          .deactivateTransactionTypeByCode(code)
          .pipe(
            tapResponse({
              next: () => {
                this.getTransactionTypes({ filter: this.get().gridConfig });
                onSuccess();
              },
              error: () => this.setError(ApiFailureText.Generic),
              finalize: () => this.patchState({ actionLoading: false }),
            }),
          ),
      ),
    ),
  );

  readonly reactivateTransactionType = this.effect<{
    code: string;
    onSuccess: () => void;
  }>((req$) =>
    req$.pipe(
      tap(() => this.patchState({ actionLoading: true })),
      switchMap(({ code, onSuccess }) =>
        this._transactionTypesService
          .reactivateTransactionTypeByCode(code)
          .pipe(
            tapResponse({
              next: () => {
                this.getTransactionTypes({ filter: this.get().gridConfig });
                onSuccess();
              },
              error: () => this.setError(ApiFailureText.Generic),
              finalize: () => this.patchState({ actionLoading: false }),
            }),
          ),
      ),
    ),
  );

  readonly downloadTemplate = this.effect<string>((code$) => {
    return code$.pipe(
      tap(() => {
        this.patchState({ isLoading: true, error: null });
      }),
      switchMap((code) =>
        this._transactionTypesService.getPreSignedTemplateURL(code).pipe(
          tap({
            next: (response) => {
              this.patchState({ isLoading: false });
              this.triggerDownload(response);
            },
            error: (error) => this.patchState({ isLoading: false, error }),
          }),
        ),
      ),
    );
  });

  readonly downloadSupportingDocument = this.effect<string>((code$) => {
    return code$.pipe(
      tap(() => {
        this.patchState({ isLoading: true, error: null });
      }),
      switchMap((code) =>
        this._transactionTypesService
          .getPreSignedSupportingDocumentURL(code)
          .pipe(
            tap({
              next: (response) => {
                this.patchState({ isLoading: false });
                this.triggerDownload(response, { target: '_blank' });
              },
              error: (error) => this.patchState({ isLoading: false, error }),
            }),
          ),
      ),
    );
  });

  private triggerDownload(url: string, options?: { target?: string }) {
    const a = document.createElement('a');
    a.href = url;
    if (options?.target) a.target = options.target;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}
